import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import {
  RadialBarChart,
  RadialBar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { toast } from "sonner"
import { apiClient } from "@/api/client"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { PortalBrandHeader } from "@/components/PortalBrandHeader"
import { PortalTermsGate } from "@/components/PortalTermsGate"
import { buyerStatusView, type DealStatus } from "@/types/deal"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExecutiveSummaryInfo {
  headline: string
  body: string
  generated_at: string
}

interface NegotiationLever {
  lever: string
  description: string
  finding_ids: number[]
}

interface BuyerDealBriefInfo {
  summary: string
  negotiation_levers: NegotiationLever[]
  generated_at: string
}

interface DealInfo {
  deal_id: string
  business_name: string
  deal_status: string
  engagement_type: string
  advisor_name: string
  loi_price: number | null
  adj_ebitda: number | null
  sde: number | null
  exit_score: number | null
  ai_risk_grade: string | null
  executive_summary?: ExecutiveSummaryInfo
  buyer_deal_brief?: BuyerDealBriefInfo
  tos_accepted?: boolean
}

interface FinancialYear {
  year?: number
  fiscal_year?: number
  period_label?: string
  revenue: number | null
  cogs?: number | null
  gross_profit?: number | null
  operating_expenses?: number | null
  net_income?: number | null
  ebitda?: number | null
  depreciation?: number | null
  amortization?: number | null
}

interface FinancialsData {
  deal_id: string
  financial_data: FinancialYear[]
  adjustments: { description: string; amount: number; category?: string; is_pending?: boolean }[]
  purchase_price: number | null
}

interface BuyerDocument {
  doc_id: string
  filename: string
  category: string
  uploaded_at: string
  status: string
}

interface QAMessage {
  role: "buyer" | "advisor"
  author: string
  text: string
  timestamp: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return "—"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

function scoreColor(score: number): string {
  if (score >= 80) return "#22c55e" // green-500
  if (score >= 60) return "#f59e0b" // amber-500
  return "#ef4444" // red-500
}

function scoreLabel(score: number): string {
  if (score >= 80) return "Strong"
  if (score >= 60) return "Moderate"
  return "Needs Attention"
}

function gradeColor(grade: string): string {
  if (grade === "A" || grade === "B") return "bg-green-100 text-green-800 border-green-300"
  if (grade === "C") return "bg-amber-100 text-amber-800 border-amber-300"
  return "bg-red-100 text-red-800 border-red-300"
}

function gradeDesc(grade: string): string {
  // QOE-TRUTH-AND-JOURNEY-1 R16 (owner correction): no possessive "your
  // advisor" — on a sell_side deal the CPA is the seller's advisor, not the
  // buyer's. Subject-less phrasing throughout, same as gradeDesc's siblings.
  const descs: Record<string, string> = {
    A: "Excellent financial health with minimal risk factors.",
    B: "Sound fundamentals with minor areas to monitor.",
    C: "Moderate risk — review diligence items carefully.",
    D: "Elevated risk — review carefully before proceeding.",
    F: "Significant concerns identified — further diligence required.",
  }
  return descs[grade] ?? "Risk assessment pending review."
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KPICard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  )
}

// ─── Summary Tab ──────────────────────────────────────────────────────────────

function SummaryTab({ info }: { info: DealInfo }) {
  const score = info.exit_score
  const grade = info.ai_risk_grade
  // QOE-TRUTH-AND-JOURNEY-1 R16: collapsed buyer-safe status, derived —
  // never a second hand-typed label list. `deal_status` is unknown/legacy
  // as a raw string until this cast; buyerStatusView returns null for
  // anything it doesn't recognize rather than guessing.
  const statusView = buyerStatusView(info.deal_status as DealStatus)

  const gaugeData = [
    { name: "score", value: score ?? 0, fill: score != null ? scoreColor(score) : "#e5e7eb" },
  ]

  return (
    <div className="space-y-6">
      {info.executive_summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{info.executive_summary.headline}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-foreground/80 whitespace-pre-wrap">
            {info.executive_summary.body}
          </CardContent>
        </Card>
      )}

      {info.buyer_deal_brief && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Negotiation Brief</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">
              {info.buyer_deal_brief.summary}
            </p>
            {info.buyer_deal_brief.negotiation_levers.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Negotiation Levers
                </p>
                <ul className="space-y-1.5">
                  {info.buyer_deal_brief.negotiation_levers.map(lever => (
                    <li key={lever.lever} className="text-sm">
                      <span className="font-medium">{lever.lever}:</span>{' '}
                      <span className="text-foreground/80">{lever.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPICard label="Asking Price" value={fmtCurrency(info.loi_price)} />
        <KPICard label="Adj. EBITDA" value={fmtCurrency(info.adj_ebitda)} />
        <KPICard label="SDE" value={fmtCurrency(info.sde)} />
        <KPICard
          label="Exit Score"
          value={score != null ? `${score} / 100` : "Pending"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Exit Score Gauge */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">Exit Score</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center py-2">
            {score != null ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <RadialBarChart
                    cx="50%"
                    cy="75%"
                    innerRadius="60%"
                    outerRadius="90%"
                    startAngle={180}
                    endAngle={0}
                    data={gaugeData}
                  >
                    <RadialBar dataKey="value" cornerRadius={6} background={{ fill: "var(--muted)" }}>
                      {gaugeData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </RadialBar>
                  </RadialBarChart>
                </ResponsiveContainer>
                <p className="text-2xl font-bold" style={{ color: scoreColor(score) }}>
                  {score}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{scoreLabel(score)}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-10">Score pending advisor review</p>
            )}
          </CardContent>
        </Card>

        {/* AI Risk Grade */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">AI Risk Grade</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-6 space-y-3">
            {grade ? (
              <>
                <span
                  className={`text-5xl font-bold border-2 rounded-xl w-20 h-20 flex items-center justify-center ${gradeColor(grade)}`}
                >
                  {grade}
                </span>
                <p className="text-xs text-muted-foreground text-center max-w-[200px]">
                  {gradeDesc(grade)}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center">Risk assessment pending</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Engagement meta */}
      <div className="text-xs text-muted-foreground space-y-0.5">
        <p>Advisor: {info.advisor_name}</p>
        <p>
          Status:{" "}
          <Badge variant="outline" className="text-[10px]">
            {statusView?.label ?? info.deal_status}
          </Badge>
        </p>
        <p>Engagement: {info.engagement_type === "buy_side" ? "Buy-side" : "Sell-side"}</p>
      </div>
      {/* QOE-TRUTH-AND-JOURNEY-1 R16 (owner correction, 2026-08-23): a
          one-line explanation of the collapsed status, from the single
          buyerStatusView mapping (frontend/src/types/deal.ts) so this can
          never drift from the seller's own spine. No possessive "your
          advisor" here — see the "Advisor:" row above for the neutral
          field label; this line is deliberately subject-less. */}
      {statusView && (
        <p className="text-xs text-muted-foreground">{statusView.detail}</p>
      )}
    </div>
  )
}

// ─── Financials Tab ───────────────────────────────────────────────────────────

function FinancialsTab({ token }: { token: string }) {
  const [data, setData] = useState<FinancialsData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiClient
      .get<FinancialsData>(`/portal/buyer/financials?token=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [token])

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const rows = data?.financial_data ?? []
  const adjustments = data?.adjustments ?? []

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Financial data is not yet available for this deal.
      </p>
    )
  }

  // Sort by year ascending
  const sorted = [...rows].sort((a, b) => {
    const ya = a.year ?? a.fiscal_year ?? 0
    const yb = b.year ?? b.fiscal_year ?? 0
    return ya - yb
  })

  // Compute adj EBITDA per year: ebitda + total non-pending adjustments
  const totalAdjs = adjustments
    .filter((a) => !a.is_pending)
    .reduce((sum, a) => sum + (a.amount ?? 0), 0)

  const sparklineData = sorted.map((r) => ({
    year: String(r.year ?? r.fiscal_year ?? r.period_label ?? ""),
    revenue: r.revenue ?? 0,
  }))

  const colLabels = sorted.map(
    (r) => `FY${r.year ?? r.fiscal_year ?? r.period_label ?? ""}`
  )

  const metricRows: { label: string; values: (number | null)[] }[] = [
    { label: "Revenue", values: sorted.map((r) => r.revenue ?? null) },
    { label: "Gross Profit", values: sorted.map((r) => r.gross_profit ?? null) },
    { label: "EBITDA", values: sorted.map((r) => r.ebitda ?? r.net_income ?? null) },
    {
      label: "Adj. EBITDA",
      values: sorted.map((r) => {
        const base = r.ebitda ?? r.net_income
        return base != null ? base + totalAdjs : null
      }),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Revenue sparkline */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">Revenue Trend</p>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={sparklineData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
            <XAxis dataKey="year" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <Tooltip
              formatter={(val: unknown) => [
                typeof val === "number" ? fmtCurrency(val) : "—",
                "Revenue",
              ]}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="var(--color-primary, #1e3a5f)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 3-year P&L table */}
      <div className="overflow-x-auto">
        <Table className="min-w-[320px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Metric</TableHead>
              {colLabels.map((lbl) => (
                <TableHead key={lbl} className="text-right">
                  {lbl}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {metricRows.map((row) => (
              <TableRow
                key={row.label}
                className={row.label === "Adj. EBITDA" ? "font-semibold" : ""}
              >
                <TableCell>{row.label}</TableCell>
                {row.values.map((v, i) => (
                  <TableCell key={i} className="text-right">
                    {fmtCurrency(v)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// ─── Documents Tab ────────────────────────────────────────────────────────────

function DocumentsTab({ dealId, token }: { dealId: string; token: string }) {
  const [docs, setDocs] = useState<BuyerDocument[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiClient
      .get<{ documents: BuyerDocument[] }>(
        `/deals/${encodeURIComponent(dealId)}/documents?audience=buyer&token=${encodeURIComponent(token)}`
      )
      .then((r) => setDocs(r.data.documents))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false))
  }, [dealId, token])

  if (loading) return <Skeleton className="h-24 w-full" />

  if (docs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {/* QOE-TRUTH-AND-JOURNEY-1 R16 (owner correction): subject-less, same
            reasoning as the status-detail line above — "your advisor" is
            wrong on a sell_side deal, where the CPA is the seller's advisor,
            not the buyer's. */}
        No documents have been released for review yet.
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {docs.map((doc) => (
        <li
          key={doc.doc_id}
          className="flex items-center justify-between p-3 border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {doc.category === "financials" ? "📊" : "📄"}
            </span>
            <div>
              <p className="font-medium text-sm">{doc.filename}</p>
              <p className="text-xs text-muted-foreground">
                {doc.category} · {fmtDate(doc.uploaded_at)}
              </p>
            </div>
          </div>
          <a
            href={`/api/deals/${encodeURIComponent(dealId)}/documents/${doc.doc_id}/download?token=${encodeURIComponent(token)}`}
            download={doc.filename}
            className="text-sm font-medium underline hover:text-primary transition-colors"
            style={{ color: "var(--color-primary)" }}
          >
            Download
          </a>
        </li>
      ))}
    </ul>
  )
}

// ─── Q&A Tab ──────────────────────────────────────────────────────────────────

function QATab({ dealId, token }: { dealId: string; token: string }) {
  const [messages, setMessages] = useState<QAMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    apiClient
      .get<QAMessage[]>(
        `/deals/${encodeURIComponent(dealId)}/qa?token=${encodeURIComponent(token)}`
      )
      .then((r) => setMessages(r.data))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false))
  }, [dealId, token])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSubmit = async () => {
    if (!text.trim()) return
    setSubmitting(true)
    try {
      const res = await apiClient.post<QAMessage & { saved: boolean }>(
        `/deals/${encodeURIComponent(dealId)}/qa?token=${encodeURIComponent(token)}`,
        { text: text.trim() }
      )
      setMessages((prev) => [...prev, res.data])
      setText("")
    } catch {
      toast.error("Failed to send question. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <Skeleton className="h-32 w-full" />

  return (
    <div className="flex flex-col gap-4">
      {/* Thread */}
      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
        {messages.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No messages yet. Ask a question below.
          </p>
        )}
        {messages.map((msg, i) => {
          const isBuyer = msg.role === "buyer"
          return (
            <div
              key={i}
              className={`flex ${isBuyer ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                  isBuyer
                    ? "text-primary-foreground rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm"
                }`}
                style={isBuyer ? { backgroundColor: "var(--color-primary)" } : undefined}
              >
                <p className="leading-relaxed">{msg.text}</p>
                <p
                  className={`text-[10px] mt-1 ${
                    isBuyer ? "text-primary-foreground/60 text-right" : "text-muted-foreground"
                  }`}
                >
                  {msg.author} · {fmtTime(msg.timestamp)}
                </p>
              </div>
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="space-y-2 border-t border-border pt-3">
        <Textarea
          placeholder="Type your question…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          className="resize-none text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit()
          }}
        />
        <Button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          className="w-full text-sm"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          {submitting ? "Sending…" : "Send Question"}
        </Button>
        <p className="text-[10px] text-muted-foreground text-center">
          Ctrl+Enter to send · Replies appear in this thread
        </p>
      </div>
    </div>
  )
}

// ─── Main BuyerPortal component ───────────────────────────────────────────────

export function BuyerPortal() {
  const location = useLocation()
  const token = new URLSearchParams(location.search).get("token") ?? ""

  const [dealInfo, setDealInfo] = useState<DealInfo | null>(null)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Re-validate on every mount (IDOR protection)
  useEffect(() => {
    if (!token) {
      // QOE-TRUTH-AND-JOURNEY-1 R16 (owner correction): no deal is loaded yet
      // at this point, so engagement_type is unknown — "your advisor" can't
      // be conditioned on it here the way the status-detail line does.
      // Subject-less phrasing sidesteps the problem instead of guessing.
      setTokenError("No access token found. Please use the link you were given.")
      setLoading(false)
      return
    }

    apiClient
      .get<DealInfo>(`/portal/buyer?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (mountedRef.current) {
          setDealInfo(r.data)
          setTosAccepted(r.data.tos_accepted ?? false)
        }
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return
        const status = (err as { response?: { status?: number } }).response?.status
        if (status === 401) {
          setTokenError(
            "This link is invalid or has expired. Please contact whoever sent it to you for a new one."
          )
        } else {
          setTokenError("Unable to load the buyer portal. Please try again later.")
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false)
      })
  }, [token])

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Buyer Due Diligence Portal" />
        <div className="flex items-center justify-center py-24">
          <p className="text-sm text-muted-foreground">Loading deal portal…</p>
        </div>
      </div>
    )
  }

  // ── Invalid token ──
  if (tokenError) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Buyer Due Diligence Portal" />
        <div className="flex items-center justify-center p-6">
          <Card className="max-w-md w-full text-center mt-16">
            <CardContent className="pt-8 pb-8 space-y-3">
              <div className="text-4xl">🔒</div>
              <h2 className="text-lg font-semibold">Access Denied</h2>
              <p className="text-sm text-muted-foreground">{tokenError}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!dealInfo) return null

  // ── ToS gate (if not accepted) ──
  if (!tosAccepted) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Buyer Due Diligence Portal" />
        <PortalTermsGate
          portalToken={token}
          role="buyer"
          onAcceptTos={() => setTosAccepted(true)}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <PortalBrandHeader label="Buyer Due Diligence Portal" />
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-base font-semibold">
            {dealInfo.business_name}
          </h1>
          <p className="text-xs text-muted-foreground">
            Buyer Due Diligence Portal · Advisor: {dealInfo.advisor_name}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <Tabs defaultValue="summary">
          <TabsList className="mb-6">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="qa">Q&amp;A</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <SummaryTab info={dealInfo} />
          </TabsContent>

          <TabsContent value="financials">
            <FinancialsTab token={token} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsTab dealId={dealInfo.deal_id} token={token} />
          </TabsContent>

          <TabsContent value="qa">
            <QATab dealId={dealInfo.deal_id} token={token} />
          </TabsContent>
        </Tabs>

        <p className="mt-8 text-[11px] text-muted-foreground text-center">
          This information is confidential. Do not share this link with others.
        </p>
        <p className="text-[11px] text-muted-foreground text-center">
          <a href="mailto:partners@yansarconsulting.com?subject=Custom%20Branding%20Request" className="underline hover:text-primary transition-colors">
            Request Custom Branding
          </a>
          {" · "}
          <a href="/privacy-policy" className="underline hover:text-primary transition-colors">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  )
}
